import React, { useState, useEffect } from 'react'

import { Button, Icon, Login, PersonAdd } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string } from 'prop-types'

import { AUTH_MODAL_EVENT } from 'Pods/LazyLoaded/AiSignInModal/constants'
import { MODE } from 'Pods/LazyLoaded/AuthModal/constants'
import LazyAuthModal from 'Pods/LazyLoaded/AuthModal/LazyAuthModal'
import i18n from 'Pods/Shared/I18n'

import ensureMTCaptcha from 'Utils/ensureMTCaptcha'

// Site-wide guest auth triggers: buy / wishlist / comment / apply controls across the
// marketplace open the auth flow by publishing these on the global event bus, and legacy
// `.js-auth-control` links funnel clicks here (auth_control.coffee). LegacyNavigation's
// UserMenu was the sole subscriber, so the new nav must listen too or guests get no auth
// UI off /ai. Extra publisher payload args are ignored, matching the legacy handlers.
const LOGIN_EVENT = 'app:login:open'
const REGISTER_EVENT = 'app:register:open'
// Icon-only glyph size on the narrowest phones (< 375px), matching the 20px
// header action icons.
const ICON_SIZE = { width: '20', height: '20' }

function isAiStudioPage() {
  if (typeof window === 'undefined') return false
  return /^(\/[a-z]{2}(?:-[a-z]{2})?)?\/(ai|ai-3d-model-generator)(\/|$)/i.test(window.location.pathname)
}

function GuestActions({ locale = 'en' }) {
  const [authModalMode, setAuthModalMode] = useState(null)

  const openAuthModal = (mode) => {
    // On /ai the auth flow is the dedicated AI sign-in modal (owned by ThreeDStudio /
    // AiSubLanding), not the marketplace AuthModal. Hand the click off via the
    // global event bus, mirroring LegacyNavigation's UserMenu.
    if (isAiStudioPage()) {
      /* eslint-disable-next-line no-undef */
      App.events.publish(AUTH_MODAL_EVENT, { mode })
      return
    }

    setAuthModalMode(mode)
  }

  useEffect(() => {
    const handleLoginOpen = () => openAuthModal(MODE.LOGIN)
    const handleRegisterOpen = () => openAuthModal(MODE.SIGNUP)

    function handleAuthControl(e) {
      if (e.target.closest('.js-auth-control')) {
        e.stopPropagation()
        e.preventDefault()
        openAuthModal(MODE.LOGIN)
        ensureMTCaptcha()
      }
    }

    document.addEventListener('click', handleAuthControl, true)

    // The legacy `App` bus is a browser global that may be absent (jsdom, or before
    // bootstrap), so guard it like useAuthModalTrigger / UpgradeHeader do. The
    // `.js-auth-control` interception above needs no bus and stays unguarded.
    const hasEventBus = typeof App !== 'undefined' && !!App.events

    if (hasEventBus) {
      App.events.subscribe(LOGIN_EVENT, handleLoginOpen)
      App.events.subscribe(REGISTER_EVENT, handleRegisterOpen)
    }

    return () => {
      document.removeEventListener('click', handleAuthControl, true)

      if (hasEventBus) {
        const unsub = App.events.unsubscribe || App.events.remove
        unsub.call(App.events, LOGIN_EVENT, handleLoginOpen)
        unsub.call(App.events, REGISTER_EVENT, handleRegisterOpen)
      }
    }
  }, [])

  const handleLoginClick = () => openAuthModal(MODE.LOGIN)
  const handleSignupClick = () => openAuthModal(MODE.SIGNUP)
  const handleAuthModalClose = () => setAuthModalMode(null)

  const loginLabel = i18n.t('navigation.user.login', { ns: 'rails', lng: locale })
  const signupLabel = i18n.t('navigation.user.register', { ns: 'rails', lng: locale })

  // Each button ships both an icon and a text label as children; SCSS shows the
  // label from 375px up and swaps to the icon-only glyph below that (Figma
  // 8242-629302). `aria-label` keeps the accessible name when the label is hidden.
  return (
    <div className="unified-nav__user">
      <Button type="secondary" size="small" aria-label={loginLabel} onClick={handleLoginClick}>
        <Icon svgComponent={Login} className="unified-nav__guest-btn-icon" size={ICON_SIZE} notSpaced />
        <span className="unified-nav__guest-btn-label">{loginLabel}</span>
      </Button>
      <Button size="small" aria-label={signupLabel} onClick={handleSignupClick}>
        <Icon svgComponent={PersonAdd} className="unified-nav__guest-btn-icon" size={ICON_SIZE} notSpaced />
        <span className="unified-nav__guest-btn-label">{signupLabel}</span>
      </Button>

      {authModalMode && (
        <LazyAuthModal mode={authModalMode} locale={locale} onClose={handleAuthModalClose} />
      )}
    </div>
  )
}

GuestActions.propTypes = {
  locale: string,
}

export default GuestActions
